import { useCallback, useEffect, useMemo, useState } from 'react'
import { AlertCircle, AlertTriangle, Lock, Save, ShieldCheck, ShieldOff } from 'lucide-react'
import { Button } from '../../lib/shadcn/button'
import { Input } from '../../lib/shadcn/input'
import { Label } from '../../lib/shadcn/label'
import { Textarea } from '../../lib/shadcn/textarea'
import { Spinner } from '../../lib/shadcn/spinner'
import { toast } from '../../lib/shadcn/sonner'
import { cn } from '../../lib/shadcn/utils'
import {
  useGetDriverProfile,
  useSaveDriverProfile,
  useRecordProfileConsent,
  useWithdrawProfileConsent,
} from '../../hooks/backend/drivers'
import { writeErrorMessage } from '../../utils/writeError'
import { readableDate } from '../../utils/coachingDates'

// The Mackay 66 on a driver record.
//
// WHAT THIS IS. Harvey Mackay's 66-question customer profile, pointed at an
// employee by his own manager, which is not what it was written for. A sales
// prospect can decline to answer. A driver whose guaranteed salary is running
// late cannot, in any way that means anything. So the screen carries three things
// the original instrument does not:
//
//   - VOLUNTEERED ONLY, on the questions where an inference would be a fabrication
//     about a man's life. Marked on the field, not buried in a policy.
//   - A CONSENT GATE on the seven questions that reach POPIA section 26: his
//     faith, his political activity, union membership and health. They cannot be
//     typed at all until his written consent is on file, and the box says why.
//   - THE GUIDANCE, out of the database, beside the question. Three of the health
//     questions carry guidance telling the manager to leave them blank, and that
//     is deliberate: they are present because they are part of the instrument, and
//     the recommendation is on the screen where he will read it.
//
// NOTHING IS DECIDED HERE. Which answers may be shown, which are locked, and
// whether consent stands all arrive from get_driver_profile_v1, which takes the
// acting person and answers per question. Deciding it in React would mean the
// answer had already crossed the wire.

type Question = {
  question_key: string
  ordinal: number
  prompt: string
  guidance: string | null
  answer_kind: string
  special_category: boolean
  special_basis: string | null
  volunteered_only: boolean
  answer: string | null
  locked: boolean
  answered_by_name: string | null
  answered_at: string | null
}

type Section = {
  section_key: string
  section_label: string
  section_ordinal: number
  answered: number
  total: number
  questions: Question[]
}

type ConsentEvent = {
  consented_at: string
  withdrawn_at: string | null
  withdrawn_reason: string | null
  how: string | null
}

type PanelData = {
  driver_name: string
  canManage: boolean
  sections: Section[]
  completeness: {
    ordinary_questions: number
    special_questions: number
    ordinary_answered: number
    special_answered: number
    special_consent: boolean
  } | null
  special_consent: {
    current: boolean
    history: ConsentEvent[]
    ever_asked: boolean
  }
}

export function Mackay66Panel({ applicationId }: { applicationId: string }) {
  const profile = useGetDriverProfile<PanelData>()
  const save = useSaveDriverProfile()
  const record = useRecordProfileConsent()
  const withdraw = useWithdrawProfileConsent()

  const trigger = profile.trigger
  const [openSection, setOpenSection] = useState<string | null>(null)
  const [edits, setEdits] = useState<Record<string, string>>({})
  const [busy, setBusy] = useState(false)
  const [consentOpen, setConsentOpen] = useState(false)
  const [consentHow, setConsentHow] = useState('')
  const [consentLanguage, setConsentLanguage] = useState('')

  const load = useCallback(() => {
    void trigger({ application_id: applicationId }, { skipCache: true })
  }, [trigger, applicationId])

  useEffect(() => {
    load()
  }, [load])

  const data = profile.data
  const sections = data?.sections ?? []
  const canManage = data?.canManage === true

  useEffect(() => {
    if (openSection == null && sections.length > 0) setOpenSection(sections[0].section_key)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sections.length])

  const answeredTotal = useMemo(
    () => sections.reduce((n, s) => n + s.answered, 0),
    [sections],
  )
  const questionTotal = useMemo(() => sections.reduce((n, s) => n + s.total, 0), [sections])

  const dirty = Object.keys(edits).length > 0

  const persist = useCallback(async () => {
    setBusy(true)
    try {
      const payload: Record<string, string | null> = {}
      for (const [k, v] of Object.entries(edits)) payload[k] = v.trim() === '' ? null : v.trim()
      const res = (await save.trigger(
        { application_id: applicationId, answers: payload },
        { skipCache: true },
      )) as { written?: number; cleared?: number } | null
      const written = res?.written ?? 0
      const cleared = res?.cleared ?? 0
      toast.success(
        cleared > 0
          ? `${written} answer(s) saved, ${cleared} cleared.`
          : `${written} answer(s) saved.`,
      )
      setEdits({})
      load()
    } catch (err) {
      // A partial save carries its counts through with the refusal, so the
      // message the database wrote already says what did land. It reaches the
      // operator unaltered.
      toast.error(writeErrorMessage(err))
      setEdits({})
      load()
    } finally {
      setBusy(false)
    }
  }, [applicationId, edits, load, save])

  const doRecordConsent = useCallback(async () => {
    setBusy(true)
    try {
      await record.trigger(
        {
          application_id: applicationId,
          how: consentHow,
          language: consentLanguage.trim() || null,
        },
        { skipCache: true },
      )
      toast.success('Consent recorded. The seven questions are now open.')
      setConsentOpen(false)
      setConsentHow('')
      setConsentLanguage('')
      load()
    } catch (err) {
      toast.error(writeErrorMessage(err))
    } finally {
      setBusy(false)
    }
  }, [applicationId, consentHow, consentLanguage, load, record])

  const doWithdraw = useCallback(async () => {
    const reason = window.prompt(
      'He is withdrawing consent. The answers to those seven questions will be permanently erased, including their history, and they do not come back if he consents again. Say why, in a sentence.',
      '',
    )
    if (reason == null) return
    setBusy(true)
    try {
      const res = (await withdraw.trigger(
        { application_id: applicationId, reason },
        { skipCache: true },
      )) as { answers_erased?: number } | null
      toast.success(`Consent withdrawn. ${res?.answers_erased ?? 0} answer(s) erased.`)
      load()
    } catch (err) {
      toast.error(writeErrorMessage(err))
    } finally {
      setBusy(false)
    }
  }, [applicationId, load, withdraw])

  if (profile.error) {
    return (
      <div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
        <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
        <span>{profile.error}</span>
      </div>
    )
  }

  if (!data && profile.loading) {
    return (
      <div className="flex items-center gap-2 p-2 text-sm text-muted-foreground">
        <Spinner className="h-4 w-4" /> Loading the profile…
      </div>
    )
  }

  if (!data) return null

  const consented = data.special_consent.current
  const lastConsent = data.special_consent.history[0] ?? null

  return (
    <div className="space-y-4">
      {/* Fill rate. The special half is counted separately and never folded in,
          because a low special count is consent working rather than neglect. */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 rounded-md border border-border bg-card p-3 text-sm">
        <span className="text-foreground">
          <span className="font-semibold tabular-nums">
            {answeredTotal} of {questionTotal}
          </span>{' '}
          answered for {data.driver_name}.
        </span>
        {data.completeness && (
          <span className="text-muted-foreground">
            {data.completeness.special_answered} of {data.completeness.special_questions} in the
            consent-gated section.
          </span>
        )}
      </div>

      {/* THE CONSENT STRIP. Never asked, consented, and withdrawn are three
          different states and the screen says which rather than showing a tick. */}
      <div
        className={cn(
          'rounded-md border p-3 text-sm',
          consented
            ? 'border-emerald-300 bg-emerald-50 text-emerald-900 dark:border-emerald-800 dark:bg-emerald-950 dark:text-emerald-200'
            : 'border-border bg-muted/40 text-foreground',
        )}
      >
        <div className="flex flex-wrap items-start gap-2">
          {consented ? (
            <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0" />
          ) : (
            <Lock className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
          )}
          <div className="min-w-0 flex-1">
            {consented ? (
              <p>
                {data.driver_name} has consented in writing to Fleetly holding the seven
                special-category answers: faith, political activity, union membership and health.
                {lastConsent?.consented_at
                  ? ` Recorded ${readableDate(lastConsent.consented_at)}.`
                  : ''}
              </p>
            ) : data.special_consent.ever_asked ? (
              <p>
                Consent was withdrawn
                {lastConsent?.withdrawn_at ? ` on ${readableDate(lastConsent.withdrawn_at)}` : ''}.
                Those answers were erased and cannot be recovered. Seven questions stay locked
                unless he consents again.
              </p>
            ) : (
              <p>
                Seven of these questions ask for information POPIA treats as special: faith,
                political activity, union membership and health. They are locked, and they stay
                locked until {data.driver_name} has given written consent. Consider leaving them
                locked: the health three add nothing Fleetly needs, because his fitness to drive
                is answered by the PrDP medical certificate.
              </p>
            )}
            {lastConsent?.how && consented && (
              <p className="mt-1 text-xs opacity-80">How it was given: {lastConsent.how}</p>
            )}
          </div>
          {canManage && (
            <div className="flex shrink-0 gap-2">
              {consented ? (
                <Button type="button" variant="outline" size="sm" disabled={busy} onClick={() => void doWithdraw()}>
                  <ShieldOff className="mr-1.5 h-3.5 w-3.5" />
                  He withdraws consent
                </Button>
              ) : (
                <Button type="button" variant="outline" size="sm" onClick={() => setConsentOpen((v) => !v)}>
                  Record his consent
                </Button>
              )}
            </div>
          )}
        </div>

        {canManage && consentOpen && !consented && (
          <div className="mt-3 space-y-2 border-t border-border pt-3">
            <div>
              <Label htmlFor="consent_how">How he gave it, and what he said</Label>
              <Textarea
                id="consent_how"
                rows={3}
                value={consentHow}
                placeholder="Read him the POPIA paragraph on 1 September and he said yes, but asked that his church not appear on any roster."
                onChange={(e) => setConsentHow(e.target.value)}
              />
              <p className="mt-1 text-xs text-muted-foreground">
                A tick is refused. He works for you and cannot easily say no, so what makes this
                consent is that somebody wrote down how it was asked and what he answered.
              </p>
            </div>
            <div className="sm:w-1/2">
              <Label htmlFor="consent_language">Which language you explained it in</Label>
              <Input
                id="consent_language"
                value={consentLanguage}
                onChange={(e) => setConsentLanguage(e.target.value)}
              />
            </div>
            <div className="flex gap-2">
              <Button
                type="button"
                size="sm"
                disabled={busy || consentHow.trim().length < 10}
                onClick={() => void doRecordConsent()}
              >
                Record consent
              </Button>
              <Button type="button" variant="ghost" size="sm" onClick={() => setConsentOpen(false)}>
                Cancel
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Section navigation. Eight sections, one open at a time, because 66 fields
          on one scroll is a form nobody finishes. */}
      <div className="flex flex-wrap gap-1">
        {sections.map((s) => (
          <button
            key={s.section_key}
            type="button"
            onClick={() => setOpenSection(s.section_key)}
            className={cn(
              'rounded-full border px-2.5 py-1 text-[11px] font-medium transition-colors',
              openSection === s.section_key
                ? 'border-[hsl(var(--brand))] bg-[hsl(var(--brand-soft))] text-[hsl(var(--brand-soft-foreground))]'
                : 'border-border text-muted-foreground hover:text-foreground',
            )}
          >
            {s.section_label}{' '}
            <span className="tabular-nums opacity-70">
              {s.answered}/{s.total}
            </span>
          </button>
        ))}
      </div>

      {sections
        .filter((s) => s.section_key === openSection)
        .map((s) => (
          <section key={s.section_key} className="space-y-3">
            {s.questions.map((q) => {
              const value = edits[q.question_key] ?? q.answer ?? ''
              const disabled = !canManage || q.locked
              return (
                <div key={q.question_key} className="rounded-lg border border-border bg-card p-3">
                  <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                    <Label htmlFor={q.question_key} className="text-sm">
                      <span className="mr-1.5 tabular-nums text-muted-foreground">{q.ordinal}.</span>
                      {q.prompt}
                    </Label>
                    {q.special_category && (
                      <span className="rounded-full border border-amber-400 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-amber-700 dark:text-amber-300">
                        {q.special_basis}
                      </span>
                    )}
                    {q.volunteered_only && (
                      <span className="rounded-full border border-border px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">
                        only what he told you
                      </span>
                    )}
                  </div>

                  {q.guidance && (
                    <p className="mt-1 text-xs text-muted-foreground">{q.guidance}</p>
                  )}

                  {q.locked ? (
                    <p className="mt-2 flex items-center gap-1.5 rounded-md border border-dashed border-border px-2 py-2 text-xs text-muted-foreground">
                      <Lock className="h-3.5 w-3.5 shrink-0" />
                      Locked until he has given written consent.
                    </p>
                  ) : q.answer_kind === 'long_text' ? (
                    <Textarea
                      id={q.question_key}
                      className="mt-2"
                      rows={2}
                      disabled={disabled}
                      value={value}
                      onChange={(e) => setEdits({ ...edits, [q.question_key]: e.target.value })}
                    />
                  ) : (
                    <Input
                      id={q.question_key}
                      className="mt-2"
                      type={q.answer_kind === 'date' ? 'date' : 'text'}
                      disabled={disabled}
                      value={value}
                      onChange={(e) => setEdits({ ...edits, [q.question_key]: e.target.value })}
                    />
                  )}

                  {q.answered_by_name && (
                    <p className="mt-1 text-[11px] text-muted-foreground">
                      {q.answered_by_name}
                      {q.answered_at ? `, ${readableDate(q.answered_at)}` : ''}
                    </p>
                  )}
                </div>
              )
            })}
          </section>
        ))}

      {/* NOT STICKY, and that was a decision rather than an omission. A sticky
          action bar over sixty-six fields covers the row a manager is typing into,
          and this repository has been bitten twice by layout that looked right in
          a screenshot. One section is at most ten questions, so the bottom of a
          section is a short scroll. */}
      {canManage && (
        <div className="-mx-3 flex flex-wrap items-center gap-2 border-t border-border bg-card px-3 pt-3 sm:-mx-4 sm:px-4">
          <Button type="button" disabled={busy || !dirty} onClick={() => void persist()}>
            {busy ? <Spinner className="mr-1.5 h-4 w-4" /> : <Save className="mr-1.5 h-4 w-4" />}
            Save {dirty ? `${Object.keys(edits).length} change(s)` : 'changes'}
          </Button>
          {dirty && (
            <Button type="button" variant="ghost" disabled={busy} onClick={() => setEdits({})}>
              Discard
            </Button>
          )}
        </div>
      )}

      {!canManage && (
        <div className="flex items-start gap-2 rounded-md border border-border bg-muted/40 p-3 text-sm text-muted-foreground">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            You can read this profile but not change it. His own manager writes it, so that what
            is on this page is what somebody who actually knows him wrote down.
          </span>
        </div>
      )}
    </div>
  )
}
